import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { 
  getPublishedGuides, 
  createGuidePost, 
  updateGuidePost, 
  deleteGuidePost 
} from '@/services/cms';

const GuideManager = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGuide, setEditingGuide] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    guideType: 'Tutorial',
    duration: '5 min read',
    level: 'Beginner',
    iconName: 'BookOpen',
    color: 'bg-cyan-500',
    featuredImage: '',
    published: true,
    seoMetadata: {
      metaTitle: '',
      metaDescription: '',
      keywords: []
    }
  });

  useEffect(() => {
    fetchGuides();
  }, []);

  const fetchGuides = async () => {
    try {
      setLoading(true);
      const data = await getPublishedGuides();
      setGuides(data);
    } catch (error) {
      console.error('Error fetching guides:', error);
      toast.error('Failed to load guides');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      // Convert keywords string to array if needed
      const submitData = {
        ...formData,
        seoMetadata: {
          ...formData.seoMetadata,
          keywords: Array.isArray(formData.seoMetadata.keywords) 
            ? formData.seoMetadata.keywords 
            : formData.seoMetadata.keywords.split(',').map(k => k.trim()).filter(k => k)
        }
      };

      if (editingGuide) {
        await updateGuidePost(editingGuide.id, submitData);
        toast.success('Guide updated successfully');
      } else {
        await createGuidePost(submitData);
        toast.success('Guide created successfully');
      }
      fetchGuides();
      resetForm();
    } catch (error) {
      console.error('Error saving guide:', error);
      toast.error(error.message || 'Failed to save guide');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (guideId) => {
    if (!window.confirm('Are you sure you want to delete this guide?')) return;
    
    try {
      await deleteGuidePost(guideId);
      toast.success('Guide deleted successfully');
      fetchGuides();
    } catch (error) {
      console.error('Error deleting guide:', error);
      toast.error('Failed to delete guide');
    }
  };

  const handleEdit = (guide) => {
    setEditingGuide(guide);
    setFormData({
      ...guide,
      seoMetadata: {
        metaTitle: guide.seoMetadata?.metaTitle || '',
        metaDescription: guide.seoMetadata?.metaDescription || '',
        keywords: Array.isArray(guide.seoMetadata?.keywords) 
          ? guide.seoMetadata.keywords.join(', ')
          : guide.seoMetadata?.keywords || ''
      }
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      description: '',
      content: '',
      guideType: 'Tutorial',
      duration: '5 min read',
      level: 'Beginner',
      iconName: 'BookOpen',
      color: 'bg-cyan-500',
      featuredImage: '',
      published: true,
      seoMetadata: {
        metaTitle: '',
        metaDescription: '',
        keywords: []
      }
    });
    setEditingGuide(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Guide Management</h2>
          <p className="text-gray-600 mt-1">Create and manage guides and tutorials</p>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)}
          className="bg-gradient-to-r from-cyan-600 to-turquoise-600"
        >
          {showForm ? (
            <>
              <X className="mr-2 w-4 h-4" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="mr-2 w-4 h-4" />
              Create New Guide
            </>
          )}
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 bg-gradient-to-br from-cyan-50 to-white">
          <h3 className="text-xl font-bold mb-4">
            {editingGuide ? 'Edit Guide' : 'Create New Guide'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                placeholder="Title *"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
              <Input
                placeholder="Slug (URL friendly) *"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                required
              />
            </div>

            <Textarea
              placeholder="Description *"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              required
            />

            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Content (Rich Text Editor) *</label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => setFormData({ ...formData, content })}
                placeholder="Write your guide content here... Use the toolbar to format text, add headings, lists, colors, etc."
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Guide Type *</label>
                <select
                  value={formData.guideType}
                  onChange={(e) => setFormData({ ...formData, guideType: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  <option value="Tutorial">Tutorial</option>
                  <option value="Guide">Guide</option>
                  <option value="Video">Video</option>
                  <option value="Download">Download</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600 mb-1 block">Level *</label>
                <select
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="All Levels">All Levels</option>
                </select>
              </div>

              <Input
                placeholder="Duration (e.g. 5 min read)"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Icon Name</label>
                <select
                  value={formData.iconName}
                  onChange={(e) => setFormData({ ...formData, iconName: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="BookOpen">BookOpen</option>
                  <option value="Rocket">Rocket</option>
                  <option value="Lightbulb">Lightbulb</option>
                  <option value="Palette">Palette</option>
                  <option value="Video">Video</option>
                  <option value="Download">Download</option>
                  <option value="Award">Award</option>
                  <option value="Target">Target</option>
                  <option value="Zap">Zap</option>
                  <option value="TrendingUp">TrendingUp</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600 mb-1 block">Color</label>
                <select
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="bg-cyan-500">Cyan</option>
                  <option value="bg-blue-500">Blue</option>
                  <option value="bg-green-500">Green</option>
                  <option value="bg-red-500">Red</option>
                  <option value="bg-yellow-500">Yellow</option>
                  <option value="bg-purple-500">Purple</option>
                  <option value="bg-pink-500">Pink</option>
                  <option value="bg-orange-500">Orange</option>
                </select>
              </div>

              <Input
                placeholder="Featured Image URL"
                value={formData.featuredImage}
                onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
              />
            </div>
            
            {/* SEO Fields */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3 text-cyan-600">SEO Settings</h4>
              <div className="space-y-4">
                <Input
                  placeholder="Meta Title"
                  value={formData.seoMetadata.metaTitle}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    seoMetadata: { ...formData.seoMetadata, metaTitle: e.target.value }
                  })}
                />
                <Textarea
                  placeholder="Meta Description"
                  value={formData.seoMetadata.metaDescription}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    seoMetadata: { ...formData.seoMetadata, metaDescription: e.target.value }
                  })}
                  rows={2}
                />
                <Input
                  placeholder="Keywords (comma-separated)"
                  value={formData.seoMetadata.keywords}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    seoMetadata: { ...formData.seoMetadata, keywords: e.target.value }
                  })}
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button 
                type="submit" 
                className="flex-1 bg-gradient-to-r from-cyan-600 to-turquoise-600"
                disabled={formLoading}
              >
                {formLoading ? 'Saving...' : (editingGuide ? 'Update Guide' : 'Create Guide')}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Guide List */}
      <div className="space-y-4">
        {guides.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-gray-600">No guides yet. Create your first one!</p>
          </Card>
        ) : (
          guides.map((guide) => (
            <Card key={guide.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex gap-4">
                {guide.featuredImage && (
                  <img 
                    src={guide.featuredImage} 
                    alt={guide.title}
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-1">{guide.title}</h3>
                      <p className="text-sm text-gray-500 mb-2">
                        <span className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded text-xs font-semibold">
                          {guide.guideType}
                        </span>
                        <span className="ml-2">{guide.level}</span>
                        <span className="ml-2">• {guide.duration}</span>
                      </p>
                      <p className="text-gray-600 line-clamp-2">{guide.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(guide)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(guide.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default GuideManager;
